import { Icone } from './Icone';

/**
 * O ÍNDICE DE TÓPICOS do relatório — vertical desde 14/09/2026.
 *
 * ⭐⭐ **A barra de cima virou índice ao lado — sistema visual 2026, parte 3.**
 * O Richard aprovou a proposta da mesa de trabalho do relatório
 * (`docs/sistema-visual-2026/proposta-relatorio-do-caso.png`): os tópicos em
 * coluna à esquerda, agrupados por capítulo, e o tópico aberto à direita.
 *
 * ⚠️ **O que NÃO mudou, e é o que ele decidiu em 08/09:**
 * - **Um tópico por vez.** Clicar num tópico troca o que aparece ao lado; a
 *   página não volta a ser a pilha longa que o confundia.
 * - **A plaquinha de atenção** marca o que ainda está em branco — *"de forma
 *   sutil e simples"* —, com o texto escondido para quem não vê a tela.
 * - **A ordem é a do documento**, nunca alfabética.
 *
 * ⭐ **O que o índice resolve que a barra não resolvia:** a barra escondia os
 * tópicos atrás de uma lista suspensa, e só dizia *quantos* faltavam em cada
 * capítulo. Em coluna, todos ficam à vista ao mesmo tempo — o que está escrito,
 * o que falta e onde se está —, sem abrir nada.
 *
 * ## A história da barra — 08/09/2026
 *
 * > *"Acredito que seja melhor organizarmos os tópicos ou seções como se fosse
 * > um menu em uma barra superior […] Cheguei a essa conclusão pois estou
 * > achando muito confuso navegar pelas seções e subseções."* — Richard
 *
 * ⛔ **O que era confuso:** as seções viviam empilhadas na mesma página, cada
 * uma com sua setinha, e para chegar à última era preciso rolar por todas.
 * A barra (modelo do CENPROT) resolveu isso com um tópico por vez — e é essa
 * decisão que o índice mantém.
 *
 * ⚠️ **Sem camada nova.** A lista suspensa saiu junto com a barra; o índice
 * não cobre nada e não entra na escada 20 · 50 · 60.
 */
export type TopicoDoMenu = {
  chave: string;
  titulo: string;
  /** O capítulo a que pertence — é ele que agrupa os tópicos no índice. */
  grupo: string;
  /** ⚠️ Vem do servidor: a mesma regra que trava a entrega. */
  respondida: boolean;
  /** A seção mãe, quando é subseção — o índice a desenha recuada. */
  paiChave?: string | null;
};

export function MenuDoRelatorio({
  topicos,
  atual,
  aoEscolher,
}: {
  topicos: TopicoDoMenu[];
  atual: string;
  aoEscolher: (chave: string) => void;
}) {
  /*
   * Os grupos, na ordem em que aparecem — que é a ordem do documento.
   * ⚠️ **Nunca alfabética:** o índice tem de ler como o relatório lê.
   */
  const grupos: { nome: string; itens: TopicoDoMenu[] }[] = [];
  for (const t of topicos) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.nome === t.grupo) ultimo.itens.push(t);
    else grupos.push({ nome: t.grupo, itens: [t] });
  }

  const escritos = topicos.filter((t) => t.respondida).length;

  return (
    <nav className="s-folha rel-indice" aria-label="Tópicos do relatório">
      <div className="rel-indice-topo">
        <span className="rel-indice-titulo">Tópicos</span>
        <span className="s-chip" title={`${escritos} de ${topicos.length} tópicos escritos`}>
          {escritos} de {topicos.length}
        </span>
      </div>

      <div className="rel-indice-lista">
        {grupos.map((g) => {
          const feitos = g.itens.filter((i) => i.respondida).length;
          return (
            <div key={g.nome} className="rel-capitulo">
              {/* ⚠️ O número do capítulo já vem no nome ("3. Análise…"). */}
              <p className="rel-capitulo-nome">
                <span>{g.nome}</span>
                <span
                  className="rel-capitulo-conta"
                  title={`${feitos} de ${g.itens.length} tópico(s) escrito(s)`}
                >
                  {feitos}/{g.itens.length}
                </span>
              </p>
              <ul>
                {g.itens.map((i) => (
                  <li key={i.chave}>
                    <button
                      type="button"
                      className={
                        'rel-topico' +
                        (i.paiChave ? ' rel-topico-sub' : '') +
                        (i.chave === atual ? ' atual' : '')
                      }
                      aria-current={i.chave === atual ? 'true' : undefined}
                      onClick={() => aoEscolher(i.chave)}
                    >
                      {/*
                        ⭐ **A plaquinha no lugar da palavra — 08/09/2026.**
                        *"De forma sutil e simples"*, foi o pedido. ⚠️ **O texto
                        continua para quem não vê a tela:** ícone sozinho não
                        carrega informação — e aqui ele é a única diferença
                        entre um tópico pronto e um por escrever.
                      */}
                      {i.respondida ? (
                        <span className="rel-marca rel-marca-feita" aria-hidden="true">
                          <Icone nome="check" tamanho={11} />
                        </span>
                      ) : (
                        <span className="rel-marca rel-marca-em-branco" title="Ainda em branco">
                          <Icone nome="atencao" tamanho={13} />
                        </span>
                      )}
                      <span className="rel-topico-nome">{i.titulo}</span>
                      {!i.respondida && <span className="so-leitor-de-tela">ainda em branco</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
